import { parseISO } from 'date-fns';
import { assert } from '../errors';
import {
    activeTransactionSchedulesFromGoalAccount,
    isPlentyGoalAccount,
} from '../financialEntities';
import { Goal } from '../goals';
import { sum } from '../util/arrays';
import { isNullish } from '../util/types';
import { makePlentyGoalAccountValueSeriesGenerator } from './generators/plentyGoalAccount';
import { arrayFromTickRange, findValueNode } from './ticks';
import {
    AccountValueNode,
    BaseGoalValueNode,
    BaseVSGContext,
    FormatValueNodeFcn,
    SnapshotBase,
    TickRange,
    ValueSeriesGenerator,
} from './types';

export type OwnershipFilter = 'MINE' | 'ALL' | 'THEIRS';

export function makeGoalVSG({
    goal,
    snapshotsByFinancialEntityId,
    ctx,
    params,
}: {
    goal: Goal;
    snapshotsByFinancialEntityId: Record<string, SnapshotBase[] | undefined>;
    ctx: BaseVSGContext;
    params?: {
        ownershipFilter?: OwnershipFilter;
    };
}): ValueSeriesGenerator<BaseGoalValueNode>;

export function makeGoalVSG<VN extends BaseGoalValueNode>({
    goal,
    snapshotsByFinancialEntityId,
    ctx,
    params,
}: {
    goal: Goal;
    snapshotsByFinancialEntityId: Record<string, SnapshotBase[] | undefined>;
    ctx: BaseVSGContext;
    params?: {
        ownershipFilter?: OwnershipFilter;
        formatValueNode: FormatValueNodeFcn<VN>;
    };
}): ValueSeriesGenerator<VN>;

export function makeGoalVSG<VN extends BaseGoalValueNode>({
    goal,
    snapshotsByFinancialEntityId,
    ctx,
    params,
}: {
    goal: Goal;
    snapshotsByFinancialEntityId: Record<string, SnapshotBase[] | undefined>;
    ctx: BaseVSGContext;
    params?: {
        ownershipFilter?: OwnershipFilter;
        formatValueNode?: FormatValueNodeFcn<VN>;
    };
}) {
    // If only the current user's accounts should be shown, filter to only
    // show FEs they own directly.

    const accounts = goal.financialEntities.filter((fe) => {
        const isJoint = goal.accountStructure === 'JOINT';
        const justMine = params?.ownershipFilter === 'MINE';
        const justTheirs = params?.ownershipFilter === 'THEIRS';

        if (isPlentyGoalAccount(fe)) {
            if (justMine && (!fe.isMine || isJoint)) return false;
            if (justTheirs && (fe.isMine || isJoint)) return false;
            return true;
        } else {
            // Must be external FE with Retirement goal.
            if (justMine && !fe.isMine) return false;
            if (justTheirs && fe.isMine) return false;
            return true;
        }
    });

    // Filter out non-goal-account FEs. Ex: external FEs attached to a
    // "Retirement" goal.
    // N.B. Separate filter expression to narrow type.
    const goalAccounts = accounts.filter(isPlentyGoalAccount);

    const vsgs = goalAccounts.map((ga) => {
        const snapshots = snapshotsByFinancialEntityId[ga.id];
        assert(snapshots, 'Missing snapshots');
        return makePlentyGoalAccountValueSeriesGenerator(
            ga,
            snapshots,
            {
                ...ctx,
                baseValueNodeParams: {
                    id: ga.id,
                },
            },
            {
                transactionSchedulePlans:
                    activeTransactionSchedulesFromGoalAccount(ga),
            }
        );
    });

    return (range: TickRange) => {
        const ticks = arrayFromTickRange(range);
        const valueSeriesArray = vsgs.map((vsg) => vsg(range));
        const values = ticks.map((tick) => {
            const tickValues: AccountValueNode[] = valueSeriesArray.map((vs) =>
                findValueNode(vs, tick)
            );
            const balance = tickValues.every((v) => isNullish(v.balance))
                ? null
                : sum(tickValues, (v) => v.balance ?? 0);
            const baseValueNode: BaseGoalValueNode = {
                ...tick,
                values: tickValues,
                balance,
                id: goal.id,
            };

            if (!params?.formatValueNode) return baseValueNode;

            return params.formatValueNode({
                goal,
                accounts,
                valueNode: baseValueNode,
            });
        });

        return { ...range, startDateObj: parseISO(range.startDate), values };
    };
}
